import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Owner-only admin page for approving/denying global hall requests.
 * Route: /admin/halls
 */
const HallsAdmin = ({ appUser, authReady }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('pending');

  // Only redirect once auth has loaded — otherwise a cold visit to
  // /admin/halls would always bounce to "/" while the status call is
  // still in flight, even for the Owner.
  useEffect(() => {
    if (!authReady) return;
    if (!appUser || appUser.status !== 'Owner') {
      navigate('/');
    }
  }, [authReady, appUser, navigate]);

  const reload = () => {
    setLoading(true);
    setError(null);
    fetch(`/api/halls/requests?status=${filter}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Failed to load'))))
      .then(data => setRequests(Array.isArray(data?.requests) ? data.requests : []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (appUser?.status === 'Owner') reload();
     
  }, [appUser?.status, filter]);

  if (!authReady) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-xl">Loading…</div>
      </div>
    );
  }

  const act = async (req, action) => {
    setBusyId(req.requestId);
    try {
      const res = await fetch(`/api/halls/requests/${req.requestId}/${action}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data?.error || `Failed to ${action} request`);
        return;
      }
      reload();
    } finally {
      setBusyId(null);
    }
  };

  if (appUser?.status !== 'Owner') {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-4">Hall Requests</h1>
      <div className="flex gap-2 mb-4 text-sm">
        {['pending', 'approved', 'denied'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded ${filter === s ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-400">Loading…</p>}
      {error && <p className="text-red-400">{error}</p>}

      {!loading && !error && requests.length === 0 && (
        <p className="text-gray-500">No {filter} requests.</p>
      )}

      <div className="space-y-2">
        {requests.map(req => (
          <div key={req.requestId} className="bg-gray-800 border border-gray-700 rounded p-4 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-blue-300">/{req.slug}</span>
                <span className="text-gray-400">·</span>
                <span className="font-semibold truncate">{req.displayName}</span>
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Requested by @{req.user?.username || `user#${req.userId}`} on{' '}
                {new Date(req.createdAt).toLocaleString()}
              </div>
            </div>

            {filter === 'pending' && (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  disabled={busyId === req.requestId}
                  onClick={() => act(req, 'approve')}
                  className="px-3 py-1.5 rounded bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm"
                >
                  Approve
                </button>
                <button
                  disabled={busyId === req.requestId}
                  onClick={() => act(req, 'deny')}
                  className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm"
                >
                  Deny
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HallsAdmin;
